import React, { FC, useState, useEffect } from "react";
import { Spin } from "antd";
import Highcharts from "highcharts";
import HighchartsStok from "highcharts/highstock";
import HighchartsVariablePie from "highcharts/modules/variable-pie";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import useHttpGet from "../../hooks/useHttpGet";
import { APIRoutes } from "../../http";
import CommonService from "../../services/common.service";
import {
  PeriodIndexTypes,
  PeriodTypesArray,
} from "../../services/enums";
import HighChartsService from "../../services/highcharts.service";
import ChartRangeSelector from "../CI10/ChartRangeSelector";

HighchartsVariablePie(Highcharts);

const getPieOptions = (data: any[]) => {
  const lastData = data?.length > 0 ? data[0] : {};

  return {
    chart: {
      type: "variablepie",
    },
    title: {
      text: "CRYPTO MARKET CAPITALIZATION - USD",
    },
    subtitle: {
      text: lastData
        ? `data on ${moment(lastData?.date).format("MM/DD/YYYY")}`
        : "",
    },
    tooltip: {
      headerFormat: "",
      pointFormatter: function () {
        const th: any = this;
        return `${th.name}: ${CommonService.toLocalNum(
          th.percentage
        )}% <br /> Market Cap: ${CommonService.toLocalNum(th.y)}`;
      },
    },
    plotOptions: {
      variablepie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          formatter: function () {
            const th: any = this;
            return `${th.key}`;
          },
        },
      },
    },
    series: [
      {
        minPointSize: '20%',
        innerSize: "80%",
        zMin: 0,
        name: "countries",
        data: data.map(f => ({ name: f?.instrument?.name ?? 'Others', y: f?.value }))
      },
    ],
  };
};

const getOptions = (data: any[]) => {
  const dataGrouped = CommonService.groupBy(data, "instrumentId");

  const chartData: any[] = [];
  const props = Object.getOwnPropertyNames(dataGrouped);
  props.forEach(f => {
    const item = dataGrouped[f];

    chartData.push({
      name: item[0]?.instrument?.name ?? 'Others',
      data: item?.map((el: any) => [
        moment(el?.date).unix() * 1000,
        el?.value,
      ])
    });
  });

  return {
    ...HighChartsService.baseSettings({
      chart: {
        type: "line",
      },
      title: {
        text: "PERCENTAGE OF TOTAL MARKET CAPITALIZATION",
      },
      subtitle: {
        text:
          data?.length > 1
            ? `updated on ${moment(data[data?.length - 1]?.date).format(
              "MM/DD/YYYY"
            )}`
            : "",
      },
      legend: {
        enabled: true,
      },
      showRangeSelector: true,
      tooltip: {
        shared: false,
        formatter: function () {
          const th: any = this;

          let s: any = `<b>Value</b> <br /> ${moment
            .unix(th?.x / 1000)
            .format("MM/DD/YYYY")}<br />`;

          let chart = this.points[0].series.chart;

          this.points.forEach(function (tooltipPoint: any) {
            if (tooltipPoint.y === 0) {
              Highcharts.setAnimation(false, chart);
              tooltipPoint.point.setState("normal");
              Highcharts.setAnimation(true, chart);
            } else {
              s +=
                '<span style="color:' +
                tooltipPoint.color +
                '">\u25CF</span> ' +
                tooltipPoint.series.name +
                ": " +
                CommonService.toLocalNum(tooltipPoint.y) +
                "<br/>";
            }
          });

          return s;
        },
      },
      series: chartData
    })
  };
};

const PercentageMarketCap: FC = () => {
  const [periodIndex, setPeriodIndex] = useState(PeriodIndexTypes["1d"]);
  const [selectedDate, setSelectedDate] = useState(moment().add(-1, 'd'));

  const { fetchedData: lastData, loading } = useHttpGet<{
    result: any;
  }>(`${APIRoutes.CRYPTO_MARKETS_CAPITALIZATION}/percentage/date/${selectedDate.format('YYYY-MM-DD')}`, {
    defaultValue: {
      result: [],
    },
    dependencies: [selectedDate]
  });

  const { fetchedData: overallData } = useHttpGet<{
    result: any;
  }>(`${APIRoutes.CRYPTO_MARKETS_CAPITALIZATION}/percentage/period/1`, {
    defaultValue: {
      result: [],
    },
  });

  useEffect(() => {
    switch (PeriodTypesArray[periodIndex]) {
      case "1w": { setSelectedDate(moment().add(-7, 'd')); break; }
      case "1m": { setSelectedDate(moment().add(-1, 'month')); break; }
      case "3m": { setSelectedDate(moment().add(-3, 'month')); break; }
      case "6m": { setSelectedDate(moment().add(-6, 'month')); break; }
      case "1y": { setSelectedDate(moment().add(-1, 'y')); break; }
      default: { setSelectedDate(moment().add(-1, 'd')); break; }
    };

  }, [periodIndex]);

  return (
    <Spin spinning={loading}>
      {lastData?.result &&
        <>
          <ChartRangeSelector
            periodIndex={periodIndex}
            setPeriodIndex={setPeriodIndex}
            showAllOption={false}
          />
          <HighchartsReact
            highcharts={Highcharts}
            options={getPieOptions(lastData?.result)}
          />
        </>
      }

      <div className="mb-4 w-100" />

      {overallData?.result
        ? <div className="mt-5 w-100" style={{ textAlign: 'center' }}>
          <Spin spinning={true} />
        </div>
        :
        <HighchartsReact
          constructorType={"stockChart"}
          highcharts={HighchartsStok}
          options={getOptions(overallData?.result || [])}
        />
      }
    </Spin>
  );
};

export default PercentageMarketCap;
